import type { StartingContext } from '@/modules/simulations/types';
import { slug } from '@/util/validation/patterns';

// Mirrors orchestrator's `startingContext` Joi schema; a mismatch here surfaces as a 400.
export const STARTING_CONTEXT_MAX_KEYS = 100;
export const STARTING_CONTEXT_MAX_BYTES = 32_768;
export const STARTING_CONTEXT_MAX_KEY_LENGTH = 128;
export const STARTING_CONTEXT_MAX_DEPTH = 100;

const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

export type StartingContextErrorCode =
  | 'invalid_json'
  | 'not_object'
  | 'invalid_key'
  | 'unsafe_key'
  | 'key_too_long'
  | 'too_many_keys'
  | 'too_large'
  | 'too_deep';

interface StartingContextError {
  code: StartingContextErrorCode;
  arg?: string | number;
}

type StartingContextResult =
  { value: StartingContext; error: null } | { value: null; error: StartingContextError };

export const STARTING_CONTEXT_ERROR_KEYS: Record<StartingContextErrorCode, string> = {
  invalid_json: 'simulations.validation_initial_context_invalid_json',
  not_object: 'simulations.validation_initial_context_not_object',
  invalid_key: 'simulations.validation_initial_context_invalid_key',
  unsafe_key: 'simulations.validation_initial_context_unsafe_key',
  key_too_long: 'simulations.validation_initial_context_key_too_long',
  too_many_keys: 'simulations.validation_initial_context_too_many_keys',
  too_large: 'simulations.validation_initial_context_too_large',
  too_deep: 'simulations.validation_initial_context_too_deep',
};

const fail = (code: StartingContextErrorCode, arg?: string | number): StartingContextResult => ({
  value: null,
  error: { code, arg },
});

const isPlainObject = (value: unknown): value is StartingContext =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// Iterative on purpose: a recursive walk would overflow on the very input this rejects.
const exceedsMaxDepth = (root: unknown): boolean => {
  const pending: [unknown, number][] = [[root, 0]];
  while (pending.length > 0) {
    const [node, depth] = pending.pop() as [unknown, number];
    if (depth >= STARTING_CONTEXT_MAX_DEPTH) {
      return true;
    }
    for (const child of Object.values(node as object)) {
      if (child !== null && typeof child === 'object') {
        pending.push([child, depth + 1]);
      }
    }
  }
  return false;
};

const keyError = (key: string): StartingContextError | null => {
  if (UNSAFE_KEYS.has(key)) {
    return { code: 'unsafe_key', arg: key };
  }
  if (key.length > STARTING_CONTEXT_MAX_KEY_LENGTH) {
    return { code: 'key_too_long', arg: STARTING_CONTEXT_MAX_KEY_LENGTH };
  }
  return slug.test(key) ? null : { code: 'invalid_key', arg: key };
};

export const parseStartingContext = (raw: string | undefined | null): StartingContextResult => {
  const trimmed = raw?.trim() ?? '';
  if (!trimmed) {
    return { value: {}, error: null };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return fail('invalid_json');
  }

  if (!isPlainObject(parsed)) {
    return fail('not_object');
  }

  // Still unwraps a pasted `{ "context": {...} }` envelope, same rule as `fromContextJson`.
  const wrapped = parsed.context;
  const context = Object.keys(parsed).length === 1 && isPlainObject(wrapped) ? wrapped : parsed;

  if (exceedsMaxDepth(context)) {
    return fail('too_deep', STARTING_CONTEXT_MAX_DEPTH);
  }

  const keys = Object.keys(context);
  if (keys.length > STARTING_CONTEXT_MAX_KEYS) {
    return fail('too_many_keys', STARTING_CONTEXT_MAX_KEYS);
  }

  for (const key of keys) {
    const error = keyError(key);
    if (error) {
      return { value: null, error };
    }
  }

  if (new TextEncoder().encode(JSON.stringify(context)).length > STARTING_CONTEXT_MAX_BYTES) {
    return fail('too_large', STARTING_CONTEXT_MAX_BYTES);
  }

  return { value: context, error: null };
};

export const stringifyStartingContext = (context: StartingContext | null | undefined): string =>
  context && Object.keys(context).length > 0 ? JSON.stringify(context, null, 2) : '';
